// Metadata espejo del editor legacy de nexus-landing.html.
// Mantener sincronizado si se agrega/quita tokens en el portal user.

export interface TokenMeta {
  group: string;
  key: string;
  label: string;
  hint: string;
}

export const THEME_TOKEN_META: TokenMeta[] = [
  { group: "Superficies", key: "bg",           label: "Fondo de página",     hint: "--mx-bg" },
  { group: "Superficies", key: "surface",      label: "Card primaria",       hint: "--mx-surface" },
  { group: "Superficies", key: "surface2",     label: "Card secundaria",     hint: "--mx-surface-2" },
  { group: "Superficies", key: "surface3",     label: "Inputs / pills",      hint: "--mx-surface-3" },
  { group: "Bordes",      key: "border",       label: "Borde sutil",         hint: "--mx-border" },
  { group: "Bordes",      key: "borderStrong", label: "Borde énfasis",       hint: "--mx-border-strong" },
  { group: "Texto",       key: "textStrong",   label: "Texto fuerte",        hint: "--mx-text-strong" },
  { group: "Texto",       key: "text",         label: "Texto principal",     hint: "--mx-text" },
  { group: "Texto",       key: "textDim",      label: "Texto secundario",    hint: "--mx-text-dim" },
  { group: "Texto",       key: "muted",        label: "Texto muted",         hint: "--mx-muted" },
  { group: "Acento",      key: "accent",       label: "Acento marca",        hint: "--mx-accent (rojo Mixcore)" },
  { group: "Acento",      key: "accent2",      label: "Acento secundario",   hint: "--mx-accent-2 (rojo claro)" },
  { group: "Acento",      key: "accentInk",    label: "Tinta sobre acento",  hint: "--mx-accent-ink (texto en botones rojos)" },
];

export interface EffectMeta {
  key: string;
  label: string;
  hint: string;
  min: number;
  max: number;
  default: number;
}

export const THEME_EFFECT_META: EffectMeta[] = [
  { key: "auroraStrength",  label: "Aurora principal (hero)",  hint: "Intensidad del degradé sup-izq del hero (--mx-aurora-strength)",  min: 0, max: 60, default: 22 },
  { key: "auroraStrength2", label: "Aurora secundaria (hero)", hint: "Intensidad del degradé inf-der del hero (--mx-aurora-strength-2)", min: 0, max: 40, default: 8 },
  { key: "glowStrength",    label: "Halo de CTAs",             hint: "Sombra accent debajo de los botones principales (--mx-glow-strength)", min: 0, max: 60, default: 25 },
];

export const DEFAULT_EFFECTS = THEME_EFFECT_META.reduce<Record<string, number>>(
  (acc, m) => { acc[m.key] = m.default; return acc; },
  {},
);

export interface TypoMeta {
  key: "serif" | "sans" | "mono";
  label: string;
  hint: string;
}

export const THEME_TYPOGRAPHY_META: TypoMeta[] = [
  { key: "serif", label: "Serif (titulares)",   hint: "--mx-serif — titulares de hero, section titles, números editoriales" },
  { key: "sans",  label: "Sans (UI / texto)",   hint: "--mx-sans — navbar, body, párrafos, botones, formularios" },
  { key: "mono",  label: "Mono (eyebrows)",     hint: "--mx-mono — eyebrows, badges, labels técnicas (FECHA, VENUE)" },
];

export interface FontEntry {
  name: string;
  stack: string;
  vibe: string;
}

// Catálogo curado de Google Fonts. Espejo de nexus-landing.html#FONT_CATALOG.
// 25 serif + 30 sans + 15 mono = 70 fuentes.
export const FONT_CATALOG: Record<"serif" | "sans" | "mono", FontEntry[]> = {
  serif: [
    { name: "DM Serif Display",   stack: "'DM Serif Display', 'Playfair Display', Georgia, serif", vibe: "Editorial moderno (default)" },
    { name: "Playfair Display",   stack: "'Playfair Display', Georgia, serif",                     vibe: "Editorial clásico, alto contraste" },
    { name: "Fraunces",           stack: "'Fraunces', Georgia, serif",                              vibe: "Editorial expresivo, formas curvas" },
    { name: "Cormorant Garamond", stack: "'Cormorant Garamond', Garamond, serif",                  vibe: "Refinado, magazine de moda" },
    { name: "Libre Bodoni",       stack: "'Libre Bodoni', 'Bodoni MT', serif",                     vibe: "Bodoni clásico, glamour" },
    { name: "Bodoni Moda",        stack: "'Bodoni Moda', 'Bodoni MT', serif",                      vibe: "Bodoni moderno, Vogue-like" },
    { name: "EB Garamond",        stack: "'EB Garamond', Garamond, serif",                          vibe: "Clásico literario, calmo" },
    { name: "Crimson Pro",        stack: "'Crimson Pro', Georgia, serif",                           vibe: "Editorial legible, neutro" },
    { name: "Lora",               stack: "'Lora', Georgia, serif",                                  vibe: "Editorial cálido" },
    { name: "Spectral",           stack: "'Spectral', Georgia, serif",                              vibe: "Editorial calmo, lectura larga" },
    { name: "Italiana",           stack: "'Italiana', Georgia, serif",                              vibe: "Italiana elegante, líneas finas" },
    { name: "Cinzel",             stack: "'Cinzel', Georgia, serif",                                vibe: "Romano ceremonial, mayúsculas" },
    { name: "Big Shoulders Display", stack: "'Big Shoulders Display', Georgia, serif",             vibe: "Brutalist vertical, agresivo" },
    { name: "Yeseva One",         stack: "'Yeseva One', Georgia, serif",                            vibe: "Display dramático" },
    { name: "Abril Fatface",      stack: "'Abril Fatface', Georgia, serif",                         vibe: "Display robusto, magazine" },
    { name: "Tenor Sans",         stack: "'Tenor Sans', Georgia, serif",                            vibe: "Editorial casi-sans, limpio" },
    { name: "Libre Baskerville",  stack: "'Libre Baskerville', Georgia, serif",                     vibe: "Editorial neutro, libro" },
    { name: "Source Serif Pro",   stack: "'Source Serif Pro', Georgia, serif",                      vibe: "Editorial sobrio, Adobe" },
    { name: "Merriweather",       stack: "'Merriweather', Georgia, serif",                          vibe: "Editorial fuerte, screen-first" },
    { name: "PT Serif",           stack: "'PT Serif', Georgia, serif",                              vibe: "Editorial neutro" },
    { name: "Cardo",              stack: "'Cardo', Garamond, serif",                                vibe: "Académico clásico" },
    { name: "Domine",             stack: "'Domine', Georgia, serif",                                vibe: "Editorial robusto, screen" },
    { name: "Forum",              stack: "'Forum', Georgia, serif",                                 vibe: "Romano elegante" },
    { name: "Marcellus",          stack: "'Marcellus', Georgia, serif",                             vibe: "Romano refinado, mayúsculas" },
    { name: "Cormorant Infant",   stack: "'Cormorant Infant', Garamond, serif",                     vibe: "Cormorant suave, curvas" },
  ],
  sans: [
    { name: "Inter Tight",        stack: "'Inter Tight', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif", vibe: "Neutral moderno (default)" },
    { name: "Inter",              stack: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",                vibe: "UI estándar moderno" },
    { name: "Manrope",            stack: "'Manrope', -apple-system, sans-serif",                                  vibe: "Geométrico humanista" },
    { name: "Plus Jakarta Sans",  stack: "'Plus Jakarta Sans', -apple-system, sans-serif",                        vibe: "Friendly tech, suave" },
    { name: "Space Grotesk",      stack: "'Space Grotesk', -apple-system, sans-serif",                            vibe: "Mono-influenced, tech editorial" },
    { name: "DM Sans",            stack: "'DM Sans', -apple-system, sans-serif",                                  vibe: "Geométrico clean" },
    { name: "Work Sans",          stack: "'Work Sans', -apple-system, sans-serif",                                vibe: "Grotesk versátil" },
    { name: "Outfit",             stack: "'Outfit', -apple-system, sans-serif",                                   vibe: "Geométrico futurista" },
    { name: "Sora",               stack: "'Sora', -apple-system, sans-serif",                                     vibe: "Geométrico minimalista" },
    { name: "Public Sans",        stack: "'Public Sans', -apple-system, sans-serif",                              vibe: "Neutral institucional" },
    { name: "Archivo",            stack: "'Archivo', -apple-system, sans-serif",                                  vibe: "Grotesk industrial" },
    { name: "IBM Plex Sans",      stack: "'IBM Plex Sans', -apple-system, sans-serif",                            vibe: "Corporate humanista" },
    { name: "Familjen Grotesk",   stack: "'Familjen Grotesk', -apple-system, sans-serif",                         vibe: "Suizo moderno, neutro" },
    { name: "Hanken Grotesk",     stack: "'Hanken Grotesk', -apple-system, sans-serif",                           vibe: "Grotesk versátil, screen" },
    { name: "Onest",              stack: "'Onest', -apple-system, sans-serif",                                    vibe: "Geométrico moderno" },
    { name: "Albert Sans",        stack: "'Albert Sans', -apple-system, sans-serif",                              vibe: "Geométrico amigable" },
    { name: "Schibsted Grotesk",  stack: "'Schibsted Grotesk', -apple-system, sans-serif",                        vibe: "Editorial nórdico, news" },
    { name: "Syne",               stack: "'Syne', -apple-system, sans-serif",                                     vibe: "Display experimental" },
    { name: "Unbounded",          stack: "'Unbounded', -apple-system, sans-serif",                                vibe: "Display ancho, brutalist" },
    { name: "Bricolage Grotesque", stack: "'Bricolage Grotesque', -apple-system, sans-serif",                     vibe: "Display variable agencia" },
    { name: "Anybody",            stack: "'Anybody', -apple-system, sans-serif",                                  vibe: "Display retro-futuro" },
    { name: "Bungee",             stack: "'Bungee', -apple-system, sans-serif",                                   vibe: "Display urban arcade" },
    { name: "Anton",              stack: "'Anton', -apple-system, sans-serif",                                    vibe: "Display condensed, póster" },
    { name: "Oswald",             stack: "'Oswald', -apple-system, sans-serif",                                   vibe: "Condensed clásico" },
    { name: "Bebas Neue",         stack: "'Bebas Neue', -apple-system, sans-serif",                               vibe: "All-caps display, poster" },
    { name: "Barlow",             stack: "'Barlow', -apple-system, sans-serif",                                   vibe: "Grotesk californiano" },
    { name: "Rubik",              stack: "'Rubik', -apple-system, sans-serif",                                    vibe: "Geométrico friendly" },
    { name: "Karla",              stack: "'Karla', -apple-system, sans-serif",                                    vibe: "Grotesk amigable" },
    { name: "Lexend",             stack: "'Lexend', -apple-system, sans-serif",                                   vibe: "Legibilidad-first" },
    { name: "Figtree",            stack: "'Figtree', -apple-system, sans-serif",                                  vibe: "Geométrico moderno cálido" },
  ],
  mono: [
    { name: "JetBrains Mono",     stack: "'JetBrains Mono', ui-monospace, 'SF Mono', Menlo, monospace", vibe: "Code-first (default)" },
    { name: "IBM Plex Mono",      stack: "'IBM Plex Mono', ui-monospace, 'SF Mono', Menlo, monospace",  vibe: "Corporate code" },
    { name: "Fira Code",          stack: "'Fira Code', ui-monospace, 'SF Mono', Menlo, monospace",      vibe: "Code con ligaduras" },
    { name: "Space Mono",         stack: "'Space Mono', ui-monospace, 'SF Mono', Menlo, monospace",     vibe: "Display mono, tech editorial" },
    { name: "Source Code Pro",    stack: "'Source Code Pro', ui-monospace, monospace",                  vibe: "Adobe code neutral" },
    { name: "Roboto Mono",        stack: "'Roboto Mono', ui-monospace, monospace",                       vibe: "Google mono geométrico" },
    { name: "DM Mono",            stack: "'DM Mono', ui-monospace, monospace",                           vibe: "Mono compacto" },
    { name: "Major Mono Display", stack: "'Major Mono Display', ui-monospace, monospace",                vibe: "Display retro-futuro" },
    { name: "Nova Mono",          stack: "'Nova Mono', ui-monospace, monospace",                         vibe: "Sci-fi mono" },
    { name: "Share Tech Mono",    stack: "'Share Tech Mono', ui-monospace, monospace",                   vibe: "Terminal retro" },
    { name: "VT323",              stack: "'VT323', ui-monospace, monospace",                             vibe: "CRT terminal vintage" },
    { name: "Cutive Mono",        stack: "'Cutive Mono', ui-monospace, monospace",                       vibe: "Máquina escribir clásica" },
    { name: "Anonymous Pro",      stack: "'Anonymous Pro', ui-monospace, monospace",                     vibe: "Code legible clásico" },
    { name: "Inconsolata",        stack: "'Inconsolata', ui-monospace, monospace",                       vibe: "Code limpio humanista" },
    { name: "Azeret Mono",        stack: "'Azeret Mono', ui-monospace, monospace",                       vibe: "Mono variable moderno" },
  ],
};

export const SYSTEM_FONT_OPTIONS: Record<"serif" | "sans" | "mono", { name: string; stack: string }> = {
  serif: { name: "Sistema (Georgia)",      stack: "Georgia, 'Times New Roman', serif" },
  sans:  { name: "Sistema (SF / Segoe)",   stack: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" },
  mono:  { name: "Sistema (SF Mono / Menlo)", stack: "ui-monospace, 'SF Mono', Menlo, Consolas, monospace" },
};

export const DEFAULT_TYPOGRAPHY = {
  serif: FONT_CATALOG.serif[0].stack,
  sans:  FONT_CATALOG.sans[0].stack,
  mono:  FONT_CATALOG.mono[0].stack,
};

export interface SliderMeta {
  key: string;
  label: string;
  hint: string;
  min: number;
  max: number;
  default: number;
}

export const THEME_SHAPE_META: SliderMeta[] = [
  { key: "radius", label: "Radio base", hint: "--mx-radius (px) — afecta cards, inputs, badges. sharp/lg escalan proporcional", min: 0, max: 32, default: 4 },
];

export const DEFAULT_SHAPE = { radius: 4 };

export const THEME_DENSITY_META: SliderMeta[] = [
  { key: "pad", label: "Padding base de cards", hint: "--mx-density-pad (px)", min: 8, max: 80, default: 28 },
  { key: "gap", label: "Gap base entre elementos", hint: "--mx-density-gap (px)", min: 4, max: 60, default: 20 },
];

export const DEFAULT_DENSITY = { pad: 28, gap: 20 };

// Defaults espejo del backend (DEFAULT_THEME_SETTINGS) — fallback si la
// plantilla no tiene tokens completos (debería ser raro).
export const FALLBACK_TOKENS = {
  dark: {
    bg: "#0A0D14", surface: "#0E131F", surface2: "#151B2A", surface3: "#1E2539",
    border: "#222A3D", borderStrong: "#38405F", muted: "#59546C", textDim: "#8B939C",
    text: "#E7E9EE", textStrong: "#FAFAFC",
    accent: "#F0354D", accent2: "#FF6A7E", accentInk: "#FAFAFC",
  },
  light: {
    bg: "#EDEEF0", surface: "#FFFFFF", surface2: "#E4E6EA", surface3: "#D9DBE0",
    border: "#D5D8DE", borderStrong: "#B8BCC4", muted: "#6E737B", textDim: "#565B63",
    text: "#1A1D24", textStrong: "#0A0D14",
    accent: "#F0354D", accent2: "#FF6A7E", accentInk: "#FFFFFF",
  },
};

// Utilidades
export function isValidColorString(v: string): boolean {
  if (typeof v !== "string") return false;
  return /^#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(v) ||
    /^rgba?\([\s\d.,%]+\)$/i.test(v);
}

export function extractFontName(stack: string): string {
  if (typeof stack !== "string") return "";
  const first = stack.split(",")[0].trim();
  return first.replace(/^['"]|['"]$/g, "");
}

export function buildGoogleFontsHref(names: string[]): string | null {
  const unique = Array.from(new Set(names.filter(Boolean)));
  if (unique.length === 0) return null;
  const families = unique
    .map((n) => "family=" + encodeURIComponent(n).replace(/%20/g, "+") + ":wght@400;600;700")
    .join("&");
  return "https://fonts.googleapis.com/css2?" + families + "&display=swap";
}
